/**
 * Pure helpers for the email editor `#` variable autocomplete.
 * Typing `#` (word-boundary) opens a searchable list; selecting still inserts
 * Jinja `{{ name }}` — `#` is only the trigger, not the stored syntax.
 */

import { labelForVariable } from '@/lib/emailTemplateVars';

/** Query after `#`: letters, digits, underscore (matches Jinja identifiers). */
const HASH_TRIGGER_RE = /#([a-zA-Z0-9_]*)$/;

/**
 * Detect an active `#query` immediately before `caretIndex` in plain text.
 * Skips mid-word hashes (e.g. `Note#1`) so template copy stays editable.
 *
 * @param {string} text
 * @param {number} caretIndex
 * @returns {{ query: string, from: number, to: number } | null}
 */
export function matchHashVariableTriggerInText(text, caretIndex) {
  const value = String(text ?? '');
  const caret = Math.max(0, Math.min(Number(caretIndex) || 0, value.length));
  const before = value.slice(0, caret);
  const match = HASH_TRIGGER_RE.exec(before);
  if (!match) return null;

  const hashIndex = before.length - match[0].length;
  if (hashIndex > 0) {
    const prev = before[hashIndex - 1];
    if (/[a-zA-Z0-9_]/.test(prev)) return null;
  }

  return { query: match[1], from: hashIndex, to: caret };
}

/**
 * Detect an active `#query` immediately before the caret in a TipTap editor.
 *
 * @param {import('@tiptap/core').Editor | null | undefined} editor
 * @returns {{ query: string, from: number, to: number } | null}
 */
export function matchHashVariableTrigger(editor) {
  if (!editor) return null;
  const { state } = editor;
  const { $from } = state.selection;
  if (!$from.parent.isTextblock) return null;

  const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc');
  const match = matchHashVariableTriggerInText(textBefore, textBefore.length);
  if (!match) return null;

  const from = $from.start() + match.from;
  const to = $from.pos;
  return { query: match.query, from, to };
}

/**
 * Replace `#query` at `range` with a Jinja merge token.
 *
 * @param {string} text
 * @param {{ from: number, to: number }} range
 * @param {string} name
 * @returns {{ text: string, caret: number }}
 */
export function replaceHashTriggerWithJinja(text, range, name) {
  const value = String(text ?? '');
  const token = `{{ ${name} }}`;
  if (!name || !range || typeof range.from !== 'number' || typeof range.to !== 'number') {
    return { text: value, caret: value.length };
  }
  const from = Math.max(0, Math.min(range.from, value.length));
  const to = Math.max(from, Math.min(range.to, value.length));
  const next = `${value.slice(0, from)}${token}${value.slice(to)}`;
  return { text: next, caret: from + token.length };
}

/**
 * Filter variable names by raw name or HR-friendly label.
 * Empty query returns the full list (stable order preserved).
 *
 * @param {string[]} variables
 * @param {string} [query]
 * @returns {string[]}
 */
export function filterVariableOptions(variables, query = '') {
  const items = [...new Set((variables || []).filter(Boolean))];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return items;
  return items.filter((name) => {
    const key = name.toLowerCase();
    const label = labelForVariable(name).toLowerCase();
    return key.includes(q) || label.includes(q);
  });
}
